// TripRateContext.tsx
import { createContext, useContext, useMemo, useCallback, useEffect, useState } from "react";
import { SessionContext } from "../Contexts/SessionContext";
import { SurveyFullViewModel } from "../TripRateNew/ViewModels/SurveyFullViewModel";
import useData from "../Utilities/Hooks/UseData";
import formatDate from "../Utilities/FormateDayjsToDateOnly";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";

export type FetchAction =
    | "GetDaysOfWeekRanges"
    | "GetServiceVehicles"
    | "SurveysToInclude"
    | "GetMainLocationTypes"
    | "GetSubLocationTypes"
    | "GetSecondaryUseClass"
    | "GetPopulationOneMile"
    | "GetPopulationFiveMile"
    | "GetCarOwnershipFiveMile"
    | "GetPtalRating"
    | "GetTravelPlan"
    | "GetPetrolFillingStation"
    | "GetResidentialUnitType";

interface TripRateContextType {
    surveys: SurveyFullViewModel[];
    loading: boolean;
    reset: () => void;
}

const TripRateContext = createContext<TripRateContextType | null>(null);
export const useTripRateContext = () => {
    const ctx = useContext(TripRateContext);
    if (!ctx) throw new Error("useTripRateContext must be used within TripRateProvider");
    return ctx;
};

const isFetchableTripRateDate = (value: string | number | Date | Dayjs | null | undefined) => {
    const parsed = dayjs(value);
    return parsed.isValid() && parsed.year() >= 1000;
};


export function TripRateProvider({ children }: { children: React.ReactNode }) {
    const { updateSessionState, sessionState } = useContext(SessionContext);
    const landuse = sessionState.tripRateMain;
    const subuse = sessionState.tripRateSub;
    const start = sessionState.landusePage.startDate;
    const end = sessionState.landusePage.endDate;
    const multiModal = sessionState.landusePage.isMultiModal ?? true;

    const surveyAction = `GetSurveyFilterList?landUse=${landuse}&subuse=${subuse}&start=${formatDate(start)}&end=${formatDate(end)}&isMultiModal=${multiModal}&v=2`;
    const canRefetchSurveys = isFetchableTripRateDate(start) && isFetchableTripRateDate(end);
    const [debouncedSurveyAction, setDebouncedSurveyAction] = useState(() =>
        canRefetchSurveys ? surveyAction : ""
    );

    useEffect(() => {
        if (!canRefetchSurveys) {
            return;
        }

        const timeoutId = setTimeout(() => {
            setDebouncedSurveyAction(surveyAction);
        }, 2000);

        return () => clearTimeout(timeoutId);
    }, [canRefetchSurveys, surveyAction]);

    const { data, loading } = useData<SurveyFullViewModel[]>("TripRateFilterNew", debouncedSurveyAction);

    const surveys = data ?? [];
    const reset = useCallback(() => updateSessionState(new (sessionState.constructor as any)()), [updateSessionState, sessionState.constructor]);

    const value: TripRateContextType = useMemo(
        () => ({ surveys, loading, reset }),
        [surveys, loading, reset]
    );

    return <TripRateContext.Provider value={value}>{children}</TripRateContext.Provider>;
}
